/**
 * Copyright (c) 2018-2026 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */

import { Program } from './webgl/program';
import { RenderableValues, Values, RenderableSchema, BaseValues } from './renderable/schema';
import { GraphicsRenderItem, ComputeRenderItem, GraphicsRenderVariant, MultiDrawBaseData, Transparency } from './webgl/render-item';
import { ValueCell } from '../mol-util/value-cell';
import { idFactory } from '../mol-util/id-factory';
import { clamp } from '../mol-math/interpolate';
import { Frustum3D } from '../mol-math/geometry/primitives/frustum3d';
import { Plane3D } from '../mol-math/geometry/primitives/plane3d';
import { Sphere3D } from '../mol-math/geometry/primitives/sphere3d';
import { Vec4 } from '../mol-math/linear-algebra/3d/vec4';
import { WebGLStats } from './webgl/context';
import { isTimingMode } from '../mol-util/debug';
import { InstanceGrid } from '../mol-math/geometry/instance-grid';
import { Clip } from '../mol-util/clip';

// avoiding namespace lookup improved performance in Chrome (Aug 2020)
const p3distanceToPoint = Plane3D.distanceToPoint;
const f3intersectsSphere3D = Frustum3D.intersectsSphere3D;
const f3containsSphere3D = Frustum3D.containsSphere3D;
const s3fromArray = Sphere3D.fromArray;

const getNextRenderableId = idFactory();

// Opaque, unforgeable token identifying "a frame" for cull-cache reuse. Deliberately an
// object identity rather than a plain number: several independent owners (Canvas3D's main
// render loop, ImagePass, PickHelper, RayHelper, ...) each mint their own frame tokens while
// culling the SAME underlying renderables - if `frame` were a plain counter, two unrelated
// owners could coincidentally reach the same integer at the same time and wrongly reuse each
// other's cull result. Object identity (`===`) makes that structurally impossible: a token
// from one owner can never equal a token from another, no coordination between owners needed.
declare const frameBrand: unique symbol;
export type Frame = { readonly [frameBrand]: never };
/** Mint a new frame token - call this whenever an owner starts a new logical "frame". */
export function createFrame(): Frame {
    return {} as Frame;
}

export type RenderableState = {
    disposed: boolean
    visible: boolean
    alphaFactor: number
    pickable: boolean
    colorOnly: boolean
    opaque: boolean
    writeDepth: boolean
}

export interface Renderable<T extends RenderableValues> {
    readonly id: number
    readonly materialId: number
    readonly values: T
    readonly state: RenderableState

    cull: (cameraPlane: Plane3D, frustum: Frustum3D, isOccluded: ((s: Sphere3D) => boolean) | null, stats: WebGLStats, frame: Frame) => void
    uncull: () => void
    cullSimple: (d: number, radius: number, scale: number) => void
    render: (variant: GraphicsRenderVariant, sharedTexturesCount: number) => void
    /** Cached clip info, `undefined` if there are no clip objects */
    getClipInfo: () => ClipInfo | undefined
    getByteCount: () => number
    getProgram: (variant: GraphicsRenderVariant) => Program
    setTransparency: (transparency: Transparency) => void
    update: () => void
    dispose: () => void
}

function getMdbData(cellCount: number, mdbData?: MultiDrawBaseData): MultiDrawBaseData {
    if (mdbData && mdbData.instanceCounts.length >= cellCount) {
        return mdbData;
    } else {
        return {
            firsts: new Int32Array(cellCount),
            counts: new Int32Array(cellCount),
            offsets: new Int32Array(cellCount),
            instanceCounts: new Int32Array(cellCount),
            baseVertices: new Int32Array(cellCount),
            baseInstances: new Uint32Array(cellCount),
            count: 0,
            uniforms: [],
        };
    }
}

//

export type LodLevelsValue = [minDistance: number, maxDistance: number, overlap: number, count: number, scale: number][]

/** The minimal set of values needed to cull a (segment of a) render item. */
export type CullValues = {
    readonly drawCount: ValueCell<number>
    readonly instanceCount: ValueCell<number>
    readonly uLod: ValueCell<Vec4>
    readonly instanceGrid: ValueCell<InstanceGrid>
    readonly boundingSphere: ValueCell<Sphere3D>
    readonly lodLevels?: ValueCell<unknown>
}

export type CullSegment = {
    /** first vertex of the segment, for non-indexed draws */
    first: number
    /** byte offset into the element buffer, for indexed draws */
    offset: number
    /** first instance of the segment in the instanced attributes */
    instanceBase: number
}

export const ZeroCullSegment: CullSegment = { first: 0, offset: 0, instanceBase: 0 };

/**
 * A multi-draw-base-data list (one entry per lod level) that can be filled
 * from multiple segments sharing one render item.
 */
export interface SegmentedMdbList {
    readonly list: MultiDrawBaseData[]
    /** resize and reset the list and refresh the per-level uLod (and, if segmented, uLodLevel) uniforms */
    prepare: (lodCell: ValueCell<unknown> | undefined, capacity: number, segmented: boolean) => LodLevelsValue | undefined
    /** append a draw entry, extending the previous entry when contiguous */
    append: (l: MultiDrawBaseData, first: number, offset: number, count: number, instanceCount: number, baseInstance: number) => void
    /**
     * Cull a segment against its instance grid and append draw entries.
     * Returns false without appending if the segment has no usable grid.
     * With `wholeSegmentFallback` gridless segments are instead culled as a
     * whole using their bounding sphere.
     */
    cullSegment: (values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue | undefined, cameraPlane: Plane3D, frustum: Frustum3D, isOccluded: ((s: Sphere3D) => boolean) | null, stats: WebGLStats, wholeSegmentFallback: boolean) => boolean
    /** append draw entries for the whole segment without culling */
    fullSegment: (values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue | undefined) => void
    /** append the draw entry for the lod level matching the given distance */
    cullSimpleSegment: (values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue, d: number, radius: number, scale: number) => void
}

export function createSegmentedMdbList(): SegmentedMdbList {
    const list: MultiDrawBaseData[] = [];
    let lodLevelsVersion = -1;

    const s = Sphere3D();

    // flattened lod tuples, avoids nested array loads in the hot cell loop
    const lodMin: number[] = [];
    const lodMax: number[] = [];
    const lodCount: number[] = [];

    function prepare(lodCell: ValueCell<unknown> | undefined, capacity: number, segmented: boolean): LodLevelsValue | undefined {
        const lodLevels = lodCell?.ref.value as LodLevelsValue | undefined;
        const hasLodLevels = !!lodLevels && lodLevels.length > 0;
        const levelCount = hasLodLevels ? lodLevels.length : 1;
        // segmented (merged) items also carry a per-instance `aSegmentLod`
        // (up to 4 levels) - members' scales can differ, unlike min/max/overlap
        const uniformCount = segmented ? 2 : 1;

        list.length = levelCount;
        const uniformsNeedUpdate = hasLodLevels && lodCell!.ref.version !== lodLevelsVersion;
        for (let j = 0; j < levelCount; ++j) {
            list[j] = getMdbData(capacity, list[j]);
            list[j].count = 0;
            if (hasLodLevels) {
                const l = list[j];
                let created = false;
                if (l.uniforms.length !== uniformCount) {
                    l.uniforms.length = uniformCount;
                    l.uniforms[0] = ['uLod', ValueCell.create(Vec4())];
                    if (segmented) l.uniforms[1] = ['uLodLevel', ValueCell.create(Math.min(j, 3))];
                    created = true;
                }
                if (uniformsNeedUpdate || created) {
                    ValueCell.update(l.uniforms[0][1], Vec4.set(l.uniforms[0][1].ref.value as Vec4, lodLevels[j][0], lodLevels[j][1], lodLevels[j][2], lodLevels[j][4]));
                }
            } else {
                list[j].uniforms.length = 0;
            }
        }
        if (uniformsNeedUpdate) lodLevelsVersion = lodCell!.ref.version;

        return hasLodLevels ? lodLevels : undefined;
    }

    function emit(l: MultiDrawBaseData, first: number, offset: number, count: number, instanceCount: number, baseInstance: number) {
        const o = l.count;
        if (o > 0 && l.firsts[o - 1] === first && l.offsets[o - 1] === offset && l.counts[o - 1] === count && l.baseInstances[o - 1] + l.instanceCounts[o - 1] === baseInstance) {
            l.instanceCounts[o - 1] += instanceCount;
        } else {
            l.firsts[o] = first;
            l.offsets[o] = offset;
            l.counts[o] = count;
            l.instanceCounts[o] = instanceCount;
            l.baseInstances[o] = baseInstance;
            l.count += 1;
        }
    }

    function cullSegment(values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue | undefined, cameraPlane: Plane3D, frustum: Frustum3D, isOccluded: ((s: Sphere3D) => boolean) | null, stats: WebGLStats, wholeSegmentFallback: boolean): boolean {
        const drawCount = values.drawCount.ref.value;
        const instanceCount = values.instanceCount.ref.value;
        if (drawCount === 0 || instanceCount === 0) return false;

        const { first, offset, instanceBase } = segment;
        const [minDistance, maxDistance] = values.uLod.ref.value;
        const hasLod = minDistance !== 0 || maxDistance !== 0;
        const grid = values.instanceGrid.ref.value;

        const levelCount = lodLevels ? lodLevels.length : 0;
        for (let j = 0; j < levelCount; ++j) {
            lodMin[j] = lodLevels![j][0];
            lodMax[j] = lodLevels![j][1];
            lodCount[j] = lodLevels![j][3];
        }

        if (grid.cellSize <= 1) {
            if (!wholeSegmentFallback) return false;

            // no instance grid for this segment, cull it as a whole
            const b = values.boundingSphere.ref.value;
            const d = p3distanceToPoint(cameraPlane, b.center);
            if (hasLod && (d + b.radius < minDistance || d - b.radius > maxDistance)) {
                if (isTimingMode) stats.culled.lod += instanceCount;
                return true;
            }
            if (!f3intersectsSphere3D(frustum, b)) {
                if (isTimingMode) stats.culled.frustum += instanceCount;
                return true;
            }
            if (isOccluded !== null && isOccluded(b)) {
                if (isTimingMode) stats.culled.occlusion += instanceCount;
                return true;
            }
            if (lodLevels) {
                // no per-cell distance to decimate by here, so draw full detail for
                // whichever level(s) match (keeps the level's uLod min/max/overlap
                // fade correct, matches the plain render path's full-detail draw for
                // ungridded objects, see the `cellSize <= 1` bail in createRenderable's cull)
                for (let j = 0, jl = lodLevels.length; j < jl; ++j) {
                    if (d + b.radius < lodLevels[j][0] || d - b.radius > lodLevels[j][1]) continue;
                    emit(list[j], first, offset, drawCount, instanceCount, instanceBase);
                }
            } else {
                emit(list[0], first, offset, drawCount, instanceCount, instanceBase);
            }
            return true;
        }

        const { cellOffsets, cellSpheres, batchOffsets, batchSpheres, batchCount, batchSize } = grid;
        const checkCellOccludedDistance = 2 * batchSize;

        for (let k = 0; k < batchCount; ++k) {
            // batchCell is serial after the grid reorder, cells of a batch are consecutive
            const cBegin = batchOffsets[k];
            const cEnd = batchOffsets[k + 1];
            const cCount = cEnd - cBegin;
            if (cCount === 0) continue;

            s3fromArray(s, batchSpheres, k * 4);
            const bd = p3distanceToPoint(cameraPlane, s.center);
            if (hasLod && (bd + s.radius < minDistance || bd - s.radius > maxDistance)) {
                if (isTimingMode) {
                    stats.culled.lod += cellOffsets[cEnd] - cellOffsets[cBegin];
                }
                continue;
            }
            if (!f3intersectsSphere3D(frustum, s)) {
                if (isTimingMode) {
                    stats.culled.frustum += cellOffsets[cEnd] - cellOffsets[cBegin];
                }
                continue;
            }
            if (isOccluded !== null && isOccluded(s)) {
                if (isTimingMode) {
                    stats.culled.occlusion += cellOffsets[cEnd] - cellOffsets[cBegin];
                }
                continue;
            }

            if (cCount === 1) {
                // single-cell batch: cell sphere equals batch sphere by construction
                // (see calcTopGrid), re-testing it per-cell would be redundant
                const begin = cellOffsets[cBegin];
                const count = cellOffsets[cBegin + 1] - begin;
                if (count === 0) continue;

                if (lodLevels) {
                    const dMax = bd + s.radius;
                    const dMin = bd - s.radius;
                    for (let j = 0; j < levelCount; ++j) {
                        if (dMax < lodMin[j] || dMin > lodMax[j]) continue;
                        emit(list[j], first, offset, lodCount[j], count, instanceBase + begin);
                    }
                } else {
                    emit(list[0], first, offset, drawCount, count, instanceBase + begin);
                }
                continue;
            }

            // cells of a fully contained batch don't need frustum tests
            const batchContained = f3containsSphere3D(frustum, s);

            for (let c = cBegin; c < cEnd; ++c) {
                const begin = cellOffsets[c];
                const end = cellOffsets[c + 1];
                const count = end - begin;
                if (count === 0) continue;

                s3fromArray(s, cellSpheres, c * 4);
                const d = p3distanceToPoint(cameraPlane, s.center);
                if (hasLod && (d + s.radius < minDistance || d - s.radius > maxDistance)) {
                    if (isTimingMode) stats.culled.lod += count;
                    continue;
                }
                if (!batchContained && !f3intersectsSphere3D(frustum, s)) {
                    if (isTimingMode) stats.culled.frustum += count;
                    continue;
                }
                if (isOccluded !== null && d - s.radius < checkCellOccludedDistance && isOccluded(s)) {
                    if (isTimingMode) stats.culled.occlusion += count;
                    continue;
                }

                if (lodLevels) {
                    const dMax = d + s.radius;
                    const dMin = d - s.radius;
                    for (let j = 0; j < levelCount; ++j) {
                        if (dMax < lodMin[j] || dMin > lodMax[j]) continue;
                        emit(list[j], first, offset, lodCount[j], count, instanceBase + begin);
                    }
                } else {
                    emit(list[0], first, offset, drawCount, count, instanceBase + begin);
                }
            }
        }
        return true;
    }

    function fullSegment(values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue | undefined) {
        const drawCount = values.drawCount.ref.value;
        const instanceCount = values.instanceCount.ref.value;
        if (drawCount === 0 || instanceCount === 0) return;

        if (lodLevels) {
            for (let j = 0, jl = lodLevels.length; j < jl; ++j) {
                emit(list[j], segment.first, segment.offset, lodLevels[j][3], instanceCount, segment.instanceBase);
            }
        } else {
            emit(list[0], segment.first, segment.offset, drawCount, instanceCount, segment.instanceBase);
        }
    }

    function cullSimpleSegment(values: CullValues, segment: CullSegment, lodLevels: LodLevelsValue, d: number, radius: number, scale: number) {
        if (values.drawCount.ref.value === 0 || values.instanceCount.ref.value === 0) return;

        for (let j = 0, jl = lodLevels.length; j < jl; ++j) {
            if (d + radius < lodLevels[j][1] * scale) {
                emit(list[j], segment.first, segment.offset, lodLevels[j][3], values.instanceCount.ref.value, segment.instanceBase);
                break;
            }
        }
    }

    return { list, prepare, append: emit, cullSegment, fullSegment, cullSimpleSegment };
}

type GraphicsRenderableValues = RenderableValues & BaseValues

export function createRenderable<T extends GraphicsRenderableValues>(renderItem: GraphicsRenderItem, values: T, state: RenderableState): Renderable<T> {
    const id = getNextRenderableId();

    const mdb = createSegmentedMdbList();
    const clipInfo = createClipInfoCache(values);
    let cullEnabled = false;
    let lastCullFrame: Frame | undefined;

    return {
        id,
        materialId: renderItem.materialId,
        values,
        state,

        cull: (cameraPlane: Plane3D, frustum: Frustum3D, isOccluded: ((s: Sphere3D) => boolean) | null, stats: WebGLStats, frame: Frame) => {
            // skip recomputation if this renderable was already culled for the current frame
            if (frame === lastCullFrame) {
                stats.cacheHits.cull++;
                return;
            }

            cullEnabled = false;

            if (values.drawCount.ref.value === 0) return;
            if (values.instanceCount.ref.value === 0) return;
            if (values.instanceGrid.ref.value.cellSize <= 1) return;

            const lodLevels = mdb.prepare(values.lodLevels, values.instanceGrid.ref.value.cellCount, false);
            mdb.cullSegment(values, ZeroCullSegment, lodLevels, cameraPlane, frustum, isOccluded, stats, false);

            cullEnabled = true;
            lastCullFrame = frame;
        },
        uncull: () => {
            cullEnabled = false;
            lastCullFrame = undefined;
        },
        cullSimple: (d: number, radius: number, scale: number) => {
            cullEnabled = false;

            const lodLevels = mdb.prepare(values.lodLevels, Math.max(1, values.instanceGrid.ref.value.cellCount), false);
            if (!lodLevels) return;

            mdb.cullSimpleSegment(values, ZeroCullSegment, lodLevels, d, radius, scale);

            cullEnabled = true;
        },
        render: (variant: GraphicsRenderVariant, sharedTexturesCount: number) => {
            if (values.uAlpha && values.alpha) {
                ValueCell.updateIfChanged(values.uAlpha, clamp(values.alpha.ref.value * state.alphaFactor, 0, 1));
            }
            renderItem.render(variant, sharedTexturesCount, cullEnabled ? mdb.list : undefined);
        },
        getClipInfo: clipInfo.get,
        getByteCount: () => renderItem.getByteCount(),
        getProgram: (variant: GraphicsRenderVariant) => renderItem.getProgram(variant),
        setTransparency: (transparency: Transparency) => renderItem.setTransparency(transparency),
        update: () => {
            renderItem.update();
            lastCullFrame = undefined;
        },
        dispose: () => renderItem.destroy()
    };
}

export type GraphicsRenderable = Renderable<GraphicsRenderableValues>

//

export type ClipInfo = {
    readonly objects: Clip.Objects
    /** Planes of the plane-type clip objects, see `Clip.getPlane`, only for the pixel variant */
    readonly planes: Plane3D[]
    /** Indices of clip objects whose surface can intersect the bounding sphere, only for the pixel variant */
    readonly capIndices: number[]
}

export interface ClipInfoCache {
    /** Clip info, recomputed when any of its inputs changed, `undefined` if there are no clip objects */
    readonly get: () => ClipInfo | undefined
}

function updateClipInfo(info: ClipInfo, values: BaseValues) {
    const { objects, planes, capIndices } = info;
    objects.count = values.dClipObjectCount.ref.value;
    objects.type = values.uClipObjectType.ref.value;
    objects.invert = values.uClipObjectInvert.ref.value;
    objects.position = values.uClipObjectPosition.ref.value;
    objects.rotation = values.uClipObjectRotation.ref.value;
    objects.scale = values.uClipObjectScale.ref.value;
    objects.transform = values.uClipObjectTransform.ref.value;

    capIndices.length = 0;
    if (values.dClipVariant.ref.value !== 'pixel') return;

    const boundingSphere = values.boundingSphere.ref.value;
    for (let i = 0; i < objects.count; ++i) {
        if (objects.type[i] === Clip.Type.plane) Clip.getPlane(planes[i] ??= Plane3D(), objects, i);
        if (Clip.canIntersectSphere(objects, i, boundingSphere)) capIndices.push(i);
    }
}

export function createClipInfoCache(values: BaseValues): ClipInfoCache {
    const cells: ValueCell<unknown>[] = [
        values.dClipObjectCount, values.dClipVariant, values.boundingSphere,
        values.uClipObjectType, values.uClipObjectInvert, values.uClipObjectPosition,
        values.uClipObjectRotation, values.uClipObjectScale, values.uClipObjectTransform,
    ];
    const versions = cells.map(() => -1);

    const info: ClipInfo = {
        objects: { count: 0, type: [], invert: [], position: [], rotation: [], scale: [], transform: [] },
        planes: [],
        capIndices: [],
    };

    function get(): ClipInfo | undefined {
        if (values.dClipObjectCount.ref.value === 0) return;

        let changed = false;
        for (let i = 0, il = cells.length; i < il; ++i) {
            const version = cells[i].ref.version;
            if (version !== versions[i]) {
                versions[i] = version;
                changed = true;
            }
        }
        if (changed) updateClipInfo(info, values);
        return info;
    }

    return { get };
}

//

export interface ComputeRenderable<T extends RenderableValues> {
    readonly id: number
    readonly values: T

    render: () => void
    update: () => void
    dispose: () => void
}

export function createComputeRenderable<T extends Values<RenderableSchema>>(renderItem: ComputeRenderItem, values: T): ComputeRenderable<T> {
    return {
        id: getNextRenderableId(),
        values,

        render: () => {
            renderItem.getProgram('compute').finalize(true);
            renderItem.render('compute', 0);
        },
        update: () => renderItem.update(),
        dispose: () => renderItem.destroy()
    };
}